import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import * as z from 'zod';
import { Input } from '@/components/ui/input';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TooltipProvider } from '@/components/ui/tooltip';

import { practitionerSchema } from '../clinic-detail/AddPractitionerSheet';
import PhoneInput from '../ui/phone-input';
import { StateInput } from '@/components/ui/state-input';
import PlacesAutocomplete from '@/components/ui/google-places-autocomplete';

const credentials = [
  'MD',
  'DVM',
  'DO',
  'DDS',
  'DPM',
  'PA',
  'NP',
  'ND',
  'CNM',
  'PA-C',
  'OD',
  'ARPN',
];

type PractitionerFormData = z.infer<typeof practitionerSchema>;

interface AddPractitionerFormProps {
  form: UseFormReturn<PractitionerFormData>;
  onSubmit: (data: PractitionerFormData) => void | Promise<void>;
  isLoading?: boolean;
  initialData?: Partial<PractitionerFormData>;
  isEdit?: boolean;
}

const AddPractitionerForm: React.FC<AddPractitionerFormProps> = ({
  form,
  isLoading = false,
  isEdit = false,
}) => {
  // Auto-populate full_name when first_name or last_name changes

  // Handle address selection from PlacesAutocomplete
  const handleAddressSelect = (addressData: any) => {
    console.log('AddPractitionerForm - handleAddressSelect called with:', addressData);
    
    if (!addressData) {
      console.error('No address data received');
      return;
    }
    
    // Set form values with shouldDirty to trigger UI update
    form.setValue('address_line_1', addressData.address_line_1 || '', { 
      shouldValidate: true, 
      shouldDirty: true,
      shouldTouch: true 
    });
    form.setValue('address_line_2', addressData.address_line_2 || '', { 
      shouldValidate: false, 
      shouldDirty: true 
    });
    form.setValue('city', addressData.city || '', { 
      shouldValidate: true, 
      shouldDirty: true,
      shouldTouch: true 
    });
    form.setValue('state', addressData.state || '', { 
      shouldValidate: true, 
      shouldDirty: true,
      shouldTouch: true 
    });
    
    // Only set postal_code if it was returned by Google Places
    if (addressData.postal_code) {
      form.setValue('postal_code', addressData.postal_code, { 
        shouldValidate: true, 
        shouldDirty: true,
        shouldTouch: true 
      });
    } else {
      form.setValue('postal_code', '', { shouldValidate: false, shouldDirty: true });
    }
    
    console.log('Form values after setting:', {
      address_line_1: form.getValues('address_line_1'),
      city: form.getValues('city'),
      state: form.getValues('state'),
      postal_code: form.getValues('postal_code'),
    });
  };

  return (
    <TooltipProvider>
      <div className='w-full space-y-6'>
        {/* Practitioner Details Section */}
        <div className='space-y-4'>
          <h3 className='text-[0.8rem] font-semibold uppercase text-teal-600 tracking-wider'>
            Practitioner Details
          </h3>

          <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
            <FormField
              control={form.control}
              name='first_name'
              render={({ field }) => (
                <FormItem className='space-y-1.5'>
                  <FormLabel className='required text-sm font-medium text-slate-700'>
                    First Name
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder='Please enter first name'
                      {...field}
                      className='h-10'
                      onChange={e => {
                        const value = e.target.value || '';
                        field.onChange(value);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='last_name'
              render={({ field }) => (
                <FormItem className='space-y-1.5'>
                  <FormLabel className='required text-sm font-medium text-slate-700'>
                    Last Name
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder='Please enter last name'
                      {...field}
                      onChange={e => {
                        const value = e.target.value || '';
                        field.onChange(value);
                      }}
                      className='h-10'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='full_name'
              disabled={true}
              render={({ field }) => (
                <FormItem className='space-y-1.5'>
                  <FormLabel className='text-sm font-medium text-slate-700'>Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder='Please enter full name' {...field} className='h-10' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='credentials'
              render={({ field }) => (
                <FormItem className='space-y-1.5'>
                  <FormLabel className='text-sm font-medium text-slate-700'>Credential</FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className='h-10'>
                        <SelectValue placeholder='Select credential' />
                      </SelectTrigger>
                      <SelectContent>
                        {credentials.map(credential => (
                          <SelectItem key={credential} value={credential}>
                            {credential}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='npi_number'
              render={({ field }) => (
                <FormItem className='space-y-1.5'>
                  <FormLabel className='required text-sm font-medium text-slate-700'>
                    NPI Number
                    {isEdit && (
                      <span className='ml-2 text-xs text-muted-foreground'>
                        (Read-only - requires re-verification)
                      </span>
                    )}
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder='Please enter NPI number'
                      disabled={isEdit}
                      maxLength={10}
                      {...field}
                      onChange={e => {
                        const value = (e.target.value || '').replace(/\D/g, '');
                        field.onChange(value);
                      }}
                      className='h-10'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='dea_number'
              render={({ field }) => (
                <FormItem className='space-y-1.5'>
                  <FormLabel className='text-sm font-medium text-slate-700'>
                    DEA Number
                    {isEdit && (
                      <span className='ml-2 text-xs text-muted-foreground'>
                        (Read-only - requires re-verification)
                      </span>
                    )}
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder='Please enter DEA number'
                      disabled={isEdit}
                      {...field}
                      onChange={e => {
                        const value = e.target.value || '';
                        field.onChange(value.trim());
                      }}
                      className='h-10'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='license_number'
              render={({ field }) => (
                <FormItem className='space-y-1.5'>
                  <FormLabel className='text-sm font-medium text-slate-700'>
                    License Number
                    {isEdit && (
                      <span className='ml-2 text-xs text-muted-foreground'>
                        (Read-only - requires re-verification)
                      </span>
                    )}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type='text'
                      placeholder='Please enter license number'
                      disabled={isEdit}
                      {...field}
                      onChange={e => {
                        const value = e.target.value || '';
                        field.onChange(value.trim());
                      }}
                      className='h-10'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='email'
              disabled={isEdit}
              render={({ field }) => (
                <FormItem className='space-y-1.5'>
                  <FormLabel className='required text-sm font-medium text-slate-700'>
                    Email
                  </FormLabel>
                  <FormControl>
                    <Input
                      type='text'
                      placeholder='Please enter email'
                      {...field}
                      onChange={e => {
                        const value = e.target.value || '';
                        field.onChange(value.trim());
                      }}
                      className='h-10'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <PhoneInput
              label='Phone'
              name='phone'
              form={form}
              placeholder='Please enter phone number'
            />

            <PhoneInput label='Fax' name='fax' form={form} placeholder='Please enter fax number' />

            <div className='md:col-span-2'>
              <PlacesAutocomplete onSelectPlace={handleAddressSelect} />
            </div>

            <FormField
              control={form.control}
              name='address_line_1'
              render={({ field }) => (
                <FormItem className='space-y-1.5'>
                  <FormLabel className='required text-sm font-medium text-slate-700'>
                    Address Line 1
                  </FormLabel>
                  <FormControl>
                    <Input
                      type='text'
                      placeholder='Please enter address line 1'
                      {...field}
                      onChange={e => {
                        const value = e.target.value || '';
                        field.onChange(value.trimStart());
                      }}
                      className='h-10'
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='address_line_2'
              render={({ field }) => (
                <FormItem className='space-y-1.5'>
                  <FormLabel className='text-sm font-medium text-slate-700'>
                    Address Line 2
                  </FormLabel>
                  <FormControl>
                    <Input
                      type='text'
                      placeholder='Please enter address line 2'
                      {...field}
                      onChange={e => {
                        const value = e.target.value || '';
                        field.onChange(value.trimStart());
                      }}
                      className='h-10'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='city'
              render={({ field }) => (
                <FormItem className='space-y-1.5'>
                  <FormLabel className='required text-sm font-medium text-slate-700'>
                    City
                  </FormLabel>
                  <FormControl>
                    <Input
                      type='text'
                      placeholder='Please enter city'
                      {...field}
                      className='h-10 bg-slate-50'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <StateInput form={form} name='state' label='State' required />

            <FormField
              control={form.control}
              name='postal_code'
              render={({ field }) => (
                <FormItem className='space-y-1.5'>
                  <FormLabel className='required text-sm font-medium text-slate-700'>
                    Postal Code
                  </FormLabel>
                  <FormControl>
                    <Input
                      type='text'
                      placeholder='Please enter postal code'
                      maxLength={5}
                      {...field}
                      onChange={e => {
                        const value = e.target.value || '';
                        field.onChange(value.trimStart());
                      }}
                      className='h-10'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default AddPractitionerForm;
